import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faChevronLeft, faChevronRight } from "@fortawesome/free-solid-svg-icons";

const Lightbox = ({ images, selectedIndex, onClose, onNext, onPrev }) => {
    // Manejar navegación por teclado
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (selectedIndex === null) return;
            if (e.key === 'Escape') onClose();
            if (e.key === 'ArrowRight') onNext();
            if (e.key === 'ArrowLeft') onPrev();
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [selectedIndex, onClose, onNext, onPrev]);

    return (
        <AnimatePresence>
            {selectedIndex !== null && (
                <motion.div 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={onClose}
                    className="fixed inset-0 z-[9999] flex items-center justify-center bg-dark-900/95 backdrop-blur-xl"
                >
                    {/* Botón Cerrar */}
                    <button 
                        onClick={(e) => { e.stopPropagation(); onClose(); }}
                        className="absolute top-4 right-4 md:top-8 md:right-8 w-12 h-12 rounded-full bg-dark-800/80 hover:bg-accent-cyan text-white flex items-center justify-center transition-colors z-[10000] border border-white/20 hover:border-transparent shadow-lg cursor-pointer"
                    >
                        <FontAwesomeIcon icon={faTimes} className="text-xl" />
                    </button>

                    {/* Botón Anterior */}
                    <button 
                        onClick={(e) => { e.stopPropagation(); onPrev(); }}
                        className="absolute left-2 md:left-8 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 rounded-full bg-dark-800/80 hover:bg-accent-blue text-white flex items-center justify-center transition-colors z-[10000] border border-white/20 hover:border-transparent shadow-lg cursor-pointer"
                    >
                        <FontAwesomeIcon icon={faChevronLeft} className="text-lg md:text-xl" />
                    </button>

                    {/* Botón Siguiente */}
                    <button 
                        onClick={(e) => { e.stopPropagation(); onNext(); }}
                        className="absolute right-2 md:right-8 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 rounded-full bg-dark-800/80 hover:bg-accent-blue text-white flex items-center justify-center transition-colors z-[10000] border border-white/20 hover:border-transparent shadow-lg cursor-pointer"
                    >
                        <FontAwesomeIcon icon={faChevronRight} className="text-lg md:text-xl" />
                    </button>

                    {/* Contenedor de la Imagen */}
                    <motion.div 
                        key={selectedIndex}
                        initial={{ opacity: 0, scale: 0.9, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.9, y: 20 }}
                        transition={{ type: "spring", stiffness: 300, damping: 30 }}
                        className="relative w-full h-full max-w-5xl px-12 md:px-24 flex flex-col items-center justify-center pointer-events-none"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <img 
                            src={images[selectedIndex].img} 
                            alt={images[selectedIndex].title} 
                            className="max-w-full max-h-[60vh] md:max-h-[70vh] object-contain rounded-lg shadow-[0_0_50px_rgba(0,0,0,0.5)] border border-white/10 pointer-events-auto"
                        />
                        <div className="mt-4 md:mt-8 text-center bg-dark-800/80 backdrop-blur-md px-4 py-3 md:px-8 md:py-4 rounded-2xl border border-white/10 pointer-events-auto max-w-[90%]">
                            <h3 className="text-xl md:text-3xl font-bold font-display text-white mb-1 md:mb-2 capitalize drop-shadow-md">
                                {images[selectedIndex].title}
                            </h3>
                            <p className="text-accent-cyan uppercase tracking-widest text-xs md:text-sm font-medium">
                                {images[selectedIndex].description}
                            </p>
                            <div className="mt-2 md:mt-3 text-slate-400 text-[10px] md:text-xs tracking-wider">
                                Imagen {selectedIndex + 1} de {images.length}
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default Lightbox;
